'use client';

import { useCallback, useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import Navbar from '../../../../components/Navbar';
import Footer from '../../../../components/Footer';
import HorizontalFilterBar from '../../../../components/HorizontalFilterBar';
import AdBanner from '../../../../components/AdBanner';
import ExpertHelpCard from '../../../../components/ExpertHelpCard';
import FilterPanel from '../../../../components/FilterPanel';
import PropertyCard from '../../../../components/PropertyCard';
import useCheckLogin from '../../../../components/CheckLogin';
import MobileListingTopBar from '../../../../components/MobileListingTopBar';
import { api } from '@/lib/apiClient';
import { useSearchParams } from 'next/navigation';

const BASE_URL = process.env.NEXT_PUBLIC_API_URL || '/api/proxy/';

const sortOptions = [
    { value: 'relevance', label: 'Relevance' },
    { value: 'newest', label: 'Newest First' },
    { value: 'price_low', label: 'Price: Low to High' },
    { value: 'price_high', label: 'Price: High to Low' },
];

function convertToArray(str) {
    if (!str) return [];
    return str.split('-').map(num => parseInt(num, 10));
}

function PropertyCardSkeleton() {
    return (
        <div className="relative p-2 md:p-3 bg-white border border-slate-100 rounded-2xl md:rounded-[20px] shadow-xs flex flex-row gap-3 md:gap-6 items-center animate-pulse">
            <div className="relative w-[110px] h-[82px] xs:w-[125px] xs:h-[94px] md:w-60 md:h-40 bg-slate-200 rounded-xl md:rounded-2xl shrink-0" />
            <div className="flex-1 min-w-0 flex flex-col h-full justify-between py-1">
                <div>
                    <div className="flex justify-between items-start">
                        <div className="h-6 w-32 bg-slate-200 rounded-md"></div>
                        <div className="w-7 h-7 rounded-full bg-slate-200 shrink-0"></div>
                    </div>
                    <div className="h-4 w-3/4 bg-slate-200 rounded-md mt-2.5"></div>
                    <div className="h-3 w-1/2 bg-slate-200 rounded-md mt-2"></div>
                </div>
                <div className="hidden md:grid grid-cols-3 gap-2 mt-4 bg-slate-50 rounded-xl p-2.5 border border-slate-100">
                    <div className="h-8 bg-slate-200/60 rounded"></div>
                    <div className="h-8 bg-slate-200/60 rounded"></div>
                    <div className="h-8 bg-slate-200/60 rounded"></div>
                </div>
                <div className="flex md:hidden gap-3 mt-2">
                    <div className="h-3 w-12 bg-slate-200 rounded"></div>
                    <div className="h-3 w-12 bg-slate-200 rounded"></div>
                </div>
            </div>
        </div>
    );
}

export default function PlotsListingPage() {
    const isLoggedIn = useCheckLogin();
    const [filters, setFilters] = useState({});
    const [sortBy, setSortBy] = useState('relevance');
    const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
    const [mockPlots, setMockPlots] = useState([]);
    const [visibleCount, setVisibleCount] = useState(3);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isFilterLoading, setIsFilterLoading] = useState(false);
    const filterTimeoutRef = useRef(null);
    const searchParams = useSearchParams();
    const cityId = searchParams.get("cityId");
    const [city_name, setCityName] = useState("");
    const [localities, setLocalities] = useState("");
    const [parms, setParms] = useState("");
    const observerRef = useRef(null);
    const sentinelRef = useRef(null);

    useEffect(() => {
        return () => {
            if (filterTimeoutRef.current) clearTimeout(filterTimeoutRef.current);
        };
    }, []);

    // Read localities from query params if any are present
    const localitiesParam = searchParams.get("localities");
    const initialLocalities = localitiesParam ? localitiesParam.split('-').map(num => parseInt(num, 10)) : [];

    const [localitiesIds, setLocalitiesIds] = useState(initialLocalities);

    useEffect(() => {
        fetchProperties();
    }, [searchParams]);

    const fetchProperties = async () => {
        try {
            setLoading(true);
            setParms(searchParams.toString());
            const localitiesArray = convertToArray(searchParams.get("localities"));
            setLocalitiesIds(localitiesArray);
            let whereClause = "property_type=6 and listing_type=$1 and city_id=$2";
            let params = ['sell', cityId];

            const res = await api.post('selectwithout/view_home_cities', { sqlData: "*", where: "id=$1", whereData: [cityId] });
            if (res?.data?.status && res?.data?.data?.[0]?.city_name) {
                setCityName(res.data.data[0].city_name);
            } else if (res?.data?.[0]?.name) {
                setCityName(res.data[0].name);
            }

            if (localitiesArray && localitiesArray.length > 0) {
                const res1 = await api.post('selectwithout/master_localities', {
                    sqlData: "locality_name as name",
                    where: "id = ANY($1)",
                    whereData: [localitiesArray],
                });
                if (res1.success === 1 && Array.isArray(res1.data)) {
                    let localitiesNames = "";
                    for (let i = 0; i < res1.data.length; i++) {
                        localitiesNames += res1.data[i].name + ", ";
                    }
                    setLocalities(localitiesNames);
                }
            } else {
                setLocalities("");
            }

            if (localitiesArray && localitiesArray.length > 0) {
                whereClause += " and locality_id = ANY($3)";
                params.push(localitiesArray);
            }

            const response = await api.post('selectwithout/view_home_listings',
                {
                    sqlData: "*",
                    where: whereClause,
                    whereData: params,
                });
            console.log("response.data", response.data);
            let property_card = [];
            if (response?.data && Array.isArray(response.data)) {
                for (let i = 0; i < response.data.length; i++) {
                    const card = response.data[i].property_card;
                    if (card) {
                        property_card.push({
                            ...card,
                            id: response.data[i].id || card.id,
                            created_at: response.data[i].created_at || card.created_at || null
                        });
                    }
                }
            }
            setMockPlots(property_card);
        } catch (error) {
            console.error('Error fetching properties:', error);
        } finally {
            setLoading(false);
        }
    };

    // Filter and Sort logic
    const filteredPlots = mockPlots.filter(plot => {
        // Plot Type
        if (filters.plotType?.length) {
            if (!filters.plotType.includes(plot.type?.toString())) {
                return false;
            }
        }

        // Plot Size
        if (filters.plotSize?.length) {
            const area = plot.area;
            if (area === undefined || area === null) return false;
            const matchesSize = filters.plotSize.some(sizeOpt => {
                if (sizeOpt.endsWith('+')) {
                    const min = parseInt(sizeOpt.slice(0, -1), 10);
                    return area >= min;
                }
                const [min, max] = sizeOpt.split('-').map(Number);
                return area >= min && area <= max;
            });
            if (!matchesSize) return false;
        }

        // Budget
        if (filters.budgetMin !== undefined) {
            let priceInLac = plot.price;
            if (plot.priceUnit === 'Cr') {
                priceInLac = plot.price * 100;
            } else if (plot.priceUnit === '₹') {
                priceInLac = plot.price / 100000;
            }
            if (priceInLac < filters.budgetMin) return false;
            if (filters.budgetMax !== null && priceInLac > filters.budgetMax) return false;
        }

        // Approval Filter
        if (filters.approval?.length) {
            const matchesApproval = filters.approval.some(opt => {
                if (opt === "cmda") {
                    return plot.is_cmda == true || plot.is_cmda == 1;
                } else if (opt === "dtcp") {
                    return plot.is_rera == true || plot.is_rera == 1;
                } else {
                    // Matches unapproved/other plots
                    const hasRera = plot.is_rera == true || plot.is_rera == 1;
                    const hasCmda = plot.is_cmda == true || plot.is_cmda == 1;
                    return !hasRera && !hasCmda;
                }
            });

            if (!matchesApproval) return false;
        }


        // const app = plot.approval?.toLowerCase() || '';
        // const matchesApproval = filters.approval.some(opt => app.includes(opt));

        // Posted By
        if (filters.postedBy?.length) {
            const posted = plot.postType?.toLowerCase() || '';
            const matchesPosted = filters.postedBy.some(opt => {
                const normalizedOpt = opt === 'owner' ? 'seller' : opt;
                return posted.includes(normalizedOpt);
            });
            if (!matchesPosted) return false;
        }

        // Facing
        if (filters.facing?.length) {
            const face = plot.facing?.toLowerCase().replace(',', '-').replace(' ', '') || '';
            const normalizedFilters = filters.facing.map(f => f.toLowerCase().replace(',', '-').replace(' ', ''));
            const matches = normalizedFilters.some(f => {
                return (face === f) || (!f.includes('-') && face.split('-').includes(f));
            });
            if (!matches) return false;
        }

        return true;
    });

    const getPlotPriceMin = (p) => {
        let price = parseFloat(p.price) || 0;
        if (p.priceUnit === 'Cr') {
            return price * 100; // price in Lac
        } else if (p.priceUnit === '₹') {
            return price / 100000; // price in Lac
        }
        return price; // price in Lac
    };

    if (sortBy === 'price_low') {
        filteredPlots.sort((a, b) => getPlotPriceMin(a) - getPlotPriceMin(b));
    } else if (sortBy === 'price_high') {
        filteredPlots.sort((a, b) => getPlotPriceMin(b) - getPlotPriceMin(a));
    } else if (sortBy === 'newest') {
        filteredPlots.sort((a, b) => {
            const timeA = a.created_at ? new Date(a.created_at).getTime() : 0;
            const timeB = b.created_at ? new Date(b.created_at).getTime() : 0;
            if (timeA && timeB) {
                return timeB - timeA;
            }
            const idA = parseInt(a.id, 10) || 0;
            const idB = parseInt(b.id, 10) || 0;
            return idB - idA;
        });
    }

    useEffect(() => {
        setVisibleCount(3);
        setIsLoadingMore(false);
    }, [filters, mockPlots, sortBy]);

    const hasMore = visibleCount < filteredPlots.length;
    const visiblePlots = filteredPlots.slice(0, visibleCount);

    const stateRef = useRef({
        hasMore,
        loading,
        isFilterLoading,
        isLoadingMore,
    });
    useEffect(() => {
        stateRef.current = {
            hasMore,
            loading,
            isFilterLoading,
            isLoadingMore,
        };
    }, [hasMore, loading, isFilterLoading, isLoadingMore]);

    const handleLoadMore = useCallback(() => {
        if (stateRef.current.isLoadingMore || !stateRef.current.hasMore) return;
        setIsLoadingMore(true);
        stateRef.current.isLoadingMore = true;
        setTimeout(() => {
            setVisibleCount(prev => prev + 3);
            setIsLoadingMore(false);
            stateRef.current.isLoadingMore = false;
        }, 400);
    }, []);

    // Callback ref for bottom sentinel
    const setLoadMoreTriggerRef = useCallback((node) => {
        if (observerRef.current) {
            observerRef.current.disconnect();
            observerRef.current = null;
        }
        sentinelRef.current = node;
        if (!node) return;
        if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') return;

        const observer = new IntersectionObserver(
            (entries) => {
                const first = entries[0];
                if (first?.isIntersecting) {
                    const { hasMore, loading, isFilterLoading, isLoadingMore } = stateRef.current;
                    if (hasMore && !loading && !isFilterLoading && !isLoadingMore) {
                        handleLoadMore();
                    }
                }
            },
            {
                root: null,
                rootMargin: '250px',
                threshold: 0.05,
            }
        );
        observer.observe(node);
        observerRef.current = observer;
    }, [handleLoadMore]);

    // Cleanup observer on unmount
    useEffect(() => {
        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, []);

    // Window scroll and resize fallback for reliable bottom detection across all mobile and desktop devices
    useEffect(() => {
        const handleScroll = () => {
            const { hasMore, loading, isFilterLoading, isLoadingMore } = stateRef.current;
            if (!hasMore || loading || isFilterLoading || isLoadingMore) return;

            const docEl = typeof document !== 'undefined' ? document.documentElement : null;
            const docBody = typeof document !== 'undefined' ? document.body : null;
            const scrollHeight = Math.max(docEl?.scrollHeight || 0, docBody?.scrollHeight || 0);
            const scrollTop = window.scrollY || window.pageYOffset || docEl?.scrollTop || docBody?.scrollTop || 0;
            const clientHeight = window.innerHeight || docEl?.clientHeight || 0;

            if (scrollHeight > 0 && scrollTop + clientHeight >= scrollHeight - 350) {
                handleLoadMore();
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('resize', handleScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleScroll);
        };
    }, [handleLoadMore]);

    const handleFilterChange = (newFilters) => {
        if (filterTimeoutRef.current) {
            clearTimeout(filterTimeoutRef.current);
        }
        setIsFilterLoading(true);
        setFilters(newFilters);
        if (typeof window !== 'undefined') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        filterTimeoutRef.current = setTimeout(() => {
            setIsFilterLoading(false);
        }, 350);
    };

    return (
        <>
            <Navbar />
            <MobileListingTopBar
                filters={filters}
                onFilterChange={handleFilterChange}
                listingType="plots"
                onSearch={(city, locs) => {
                    const params = new URLSearchParams();
                    if (city?.id) params.set('cityId', city.id);
                    if (locs?.length > 0) params.set('localities', locs.map(l => l.id).join('-'));
                    const citySlug = city?.name?.toLowerCase() || 'all';
                    window.location.href = `/plots/${citySlug}?${params.toString()}`;
                }}
            />
            <main className="min-h-screen bg-gray pb-20 lg:pb-0 mb-8">
                {/* Horizontal Filter Bar - Sticky */}
                <div className="hidden lg:block sticky top-16 z-40 horizontal-filter-bar">
                    <HorizontalFilterBar
                        filters={filters}
                        onChange={handleFilterChange}
                        listingType="plots"
                    />
                </div>

                {/* Page Header */}
                <div className="border-b border-gray-100">
                    <div className="container-custom py-2">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mt-5 mb-3">
                            <nav className="flex items-center gap-2 text-sm text-gray-500" aria-label="Breadcrumb">
                                <span className="text-gray-900 font-medium">
                                    {loading || isFilterLoading ? (
                                        <span className="text-gray-500 flex items-center gap-2">
                                            <span className="w-3.5 h-3.5 border-2 border-primary border-t-transparent rounded-full animate-spin"></span>
                                            Updating properties in {city_name || 'selected city'}...
                                        </span>
                                    ) : (
                                        <>
                                            <strong> Available {filteredPlots.length} Properties </strong>  for Sale in {localities} {city_name}
                                        </>
                                    )}
                                </span>
                            </nav>
                            {/* RIGHT – Relevance Select */}
                            <div className="flex items-center justify-between md:justify-end gap-2 w-full md:w-auto shrink-0">
                                <label htmlFor="sortBy" className="text-sm text-gray-600 whitespace-nowrap">
                                    Sort by:
                                </label>
                                <select
                                    id="sortBy"
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                    className="h-9 w-full md:w-auto text-sm px-2 rounded-md bg-white border border-gray-300 text-gray-700 shadow-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                                >
                                    {sortOptions.map((opt) => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Content Split Layout */}
                <div className="container-custom">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 py-0 items-start">

                        {/* Left: Plots List (8 cols) */}
                        <div className="lg:col-span-8 flex flex-col gap-3">
                            {loading || isFilterLoading ? (
                                <div className="flex flex-col gap-3">
                                    {[1, 2, 3].map((n) => (
                                        <PropertyCardSkeleton key={n} />
                                    ))}
                                </div>
                            ) : filteredPlots.length === 0 ? (
                                <div className="bg-white rounded-2xl p-10 text-center border border-slate-100 shadow-xs flex flex-col items-center justify-center">
                                    <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center text-2xl mb-3">
                                        🏡
                                    </div>
                                    <h3 className="text-base font-bold text-slate-800 mb-1">
                                        No Plots Found
                                    </h3>
                                    <p className="text-xs text-slate-500 max-w-sm mb-4">
                                        We couldn't find any plots matching your selected criteria in {city_name || 'this city'}. Try adjusting your filters.
                                    </p>
                                    {Object.keys(filters).length > 0 && (
                                        <button
                                            onClick={() => handleFilterChange({})}
                                            className="px-4 py-2 text-xs font-semibold text-primary bg-primary/10 hover:bg-primary/20 rounded-xl transition-colors cursor-pointer"
                                        >
                                            Reset All Filters
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <>
                                    {/* Plots List Cards */}
                                    {visiblePlots.map((plot) => (
                                        <PropertyCard
                                            key={plot.id}
                                            property={plot}
                                            params={parms}
                                            listingType="plots"
                                            variant="horizontal"
                                        />
                                    ))}

                                    {/* Bottom Sentinel & Loading indicator on scroll */}
                                    {hasMore && (
                                        <div ref={setLoadMoreTriggerRef} className="py-4 flex flex-col items-center justify-center gap-3">
                                            {isLoadingMore ? (
                                                <>
                                                    <div className="w-full flex flex-col gap-3">
                                                        <PropertyCardSkeleton />
                                                    </div>
                                                    <div className="flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-white border border-slate-200/80 shadow-xs text-slate-600 text-xs font-medium">
                                                        <span className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></span>
                                                        <span>Loading more plots...</span>
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="h-6 w-full flex items-center justify-center">
                                                    <span className="text-[11px] text-slate-400">Scroll down to load more</span>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {!hasMore && filteredPlots.length > 3 && (
                                        <div className="py-6 text-center">
                                            <p className="text-xs font-medium text-slate-400 flex items-center justify-center gap-1.5">
                                                <svg className="w-4 h-4 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                                You've viewed all {filteredPlots.length} properties
                                            </p>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Right: Ad Sidebar (4 cols) */}
                        <aside className="lg:col-span-4 hidden lg:flex flex-col gap-6 sticky top-24">
                            {/* Featured Widget */}
                            <ExpertHelpCard
                                title="Plot Buying Guide"
                                description="Confused about DTCP vs CMDA? Download our free guide on how to safely buy land in Chennai."
                                buttonText="Post Property"
                                link={isLoggedIn ? "/seller/post-property" : "/register"}
                                bgClass="bg-gradient-to-br from-blue-50 to-cyan-50/50"
                                borderClass="border-slate-100"
                                titleColorClass="text-blue-900"
                                descColorClass="text-blue-900"
                                btnTextColorClass="text-blue-600 group-hover:text-blue-800"
                                glowColor1="#1051b9ff"
                                glowColor2="#2d46d4ff"
                            />

                            {/* Ad Banners */}
                            <AdBanner
                                type="plots"
                                noofads={2}
                            />
                        </aside>

                    </div>
                </div>

                {/* Mobile Filter Modal */}
                {isMobileFilterOpen && (
                    <div className="fixed inset-0 z-50 lg:hidden">
                        <div
                            className="absolute inset-0 bg-black/30 backdrop-blur-sm"
                            onClick={() => setIsMobileFilterOpen(false)}
                        />
                        <div className="absolute right-0 top-0 bottom-0 w-[80%] bg-white overflow-y-auto shadow-2xl z-10">
                            <FilterPanel
                                filters={filters}
                                onChange={handleFilterChange}
                                listingType="plots" // Pass plots type
                                isMobile={true}
                                onClose={() => setIsMobileFilterOpen(false)}
                            />
                        </div>
                    </div>
                )}
            </main>
            <Footer />
        </>
    );
}
